// src/hooks/useAssistant.ts

import { useState, useEffect } from 'react';
import { useUnidades } from 'context/UnidadeContext';
import { useRelatorios } from 'context/RelatorioContext';
import { useAprendizado } from 'context/AprendizadoContext';
import { useItens } from 'context/ItensContext';
import { useDevices } from 'context/DeviceContext';
import { collection, getDocs, query } from 'firebase/firestore';
import { db } from 'services/firebase';
import { useAuth } from 'context/AuthContext';

interface Solicitacao {
  dispositivoId: string;
  Produto: string;
  Data: string;
}

export const useAssistant = () => {
  const { user } = useAuth();
  const { unidades } = useUnidades();
  const { relatorios } = useRelatorios();
  const { aprendizadoData } = useAprendizado();
  const { itens } = useItens();
  const { devices } = useDevices();
  const [solicitacoes, setSolicitacoes] = useState<Solicitacao[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  useEffect(() => {
    const fetchSolicitacoes = async () => {
      if (!user?.uid) {
        setSolicitacoes([]);
        return;
      }
      try {
        const solicitacoesCollectionRef = collection(db, `clientes/${user.uid}/solicitacoes`);
        const data = await getDocs(query(solicitacoesCollectionRef));
        setSolicitacoes(data.docs.map((doc) => ({ ...doc.data() })) as Solicitacao[]);
      } catch (error) {
        console.error("Assistente: Erro ao buscar solicitações.", error);
      }
    };
    if (user) {
      fetchSolicitacoes();
    }
  }, [user]);

  useEffect(() => {
    const generateSuggestions = () => {
      const newSuggestions: string[] = [];

      // --- REGRA 1: Análise de Desempenho das Unidades ---
      unidades.forEach(unidade => {
        const relatoriosDaUnidade = relatorios.filter(r => r.dispositivoId === unidade.dispositivoId).sort((a, b) => new Date(b.Data).getTime() - new Date(a.Data).getTime());
        if (relatoriosDaUnidade.length === 0) return;

        const ultimoRelatorio = relatoriosDaUnidade[0];
        const dataUltimoRelatorio = new Date(ultimoRelatorio.Data + "T12:00:00");
        const diaSemanaUltimo = ["dom.", "seg.", "ter.", "qua.", "qui.", "sex.", "sab."][dataUltimoRelatorio.getDay()];
        
        const aprendizado = aprendizadoData.find(a => a.dispositivoId === unidade.dispositivoId);
        if (!aprendizado) return;

        const relatoriosAprendizado = relatorios.filter(r => r.dispositivoId === unidade.dispositivoId && r.DiaSemana === diaSemanaUltimo);
        const metaDiaria = relatoriosAprendizado.length > 0 ? relatoriosAprendizado.reduce((sum, r) => sum + r.contador, 0) / relatoriosAprendizado.length : 0;
        if (metaDiaria === 0) return;

        const contagemDoDia = relatoriosDaUnidade.filter(r => r.Data === ultimoRelatorio.Data).reduce((sum, r) => sum + r.contador, 0);
        const progresso = (contagemDoDia / metaDiaria) * 100;

        if (progresso < 60) {
          newSuggestions.push(`A unidade ${unidade.nome} está com fluxo muito abaixo do histórico.`);
        }
        if (progresso >= 140) {
          newSuggestions.push(`O fluxo na unidade ${unidade.nome} está excepcionalmente alto! Verifique se a equipe está preparada para a alta demanda.`);
        }
      });
      
      setSuggestions(newSuggestions);
    };

    if (unidades.length > 0 && relatorios.length > 0 && itens.length > 0 && devices.length > 0 && solicitacoes.length > 0) {
      generateSuggestions();
    }

  }, [unidades, relatorios, aprendizadoData, itens, devices, solicitacoes]);

  return { suggestions };
};
